import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Clock, Newspaper, ChevronRight, Radio } from "lucide-react";
import { API } from "../../shared/config";
import { pageOfScreen } from "../../navigation/navConfig";
import { useLues } from "../../navigation/actualitesLues";
import FilDuJourDetail from "./FilDuJourDetail";

const TONES = ['cyan', 'amber'];

const Event = ({ n, label, detail, items = [], onClick, onItem, testId, delay, tone, icon, alert }) => (
  <li className={`opn-tl__ev ${(alert ?? n > 0) ? 'is-alert' : ''} ${items.length ? 'has-branch' : ''}`} style={{ animationDelay: `${delay}ms`, '--tl-c': tone === 'amber' ? 'var(--tl-alert)' : 'var(--tl-calm)' }}>
    <span className="opn-tl__node" />
    <button type="button" onClick={onClick} disabled={!onClick} data-testid={testId}>
      <span className="opn-tl__n">{n || 0}</span>
      <span className="opn-tl__txt"><span className="opn-tl__label">{icon}{label}</span>{detail && <small className="opn-tl__detail">{detail}</small>}</span>
      <ChevronRight className="opn-tl__go h-4 w-4" />
    </button>
    {items.length > 0 && (
      <ul className="opn-tl__branch">
        {items.map((it, i) => (
          <li key={i} className={it.id && onItem ? 'is-link' : ''}>
            <span className="opn-tl__sub-dot" />
            {it.id && onItem
              ? <button type="button" className="opn-tl__sub-btn" onClick={() => onItem(it)} title="Ouvrir la fiche" data-testid={`${testId}-item-${it.id}`}><span className="opn-tl__sub-t">{it.t}</span>{it.s && <small>{it.s}</small>}</button>
              : <><span className="opn-tl__sub-t">{it.t}</span>{it.s && <small>{it.s}</small>}</>}
          </li>
        ))}
      </ul>
    )}
  </li>
);

const FilDuJour = ({ tree, user, onGoScreen }) => {
  const [data, setData] = useState(null);
  const [majA, setMajA] = useState(null);
  const [selected, setSelected] = useState(null);
  const refresh = useCallback(() => {
    axios.get(`${API}/accueil/fil-du-jour`).then(r => { setData(r.data); setMajA(new Date()); }).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 60000);
    window.addEventListener('nav-badges-refresh', refresh);
    return () => { clearInterval(id); window.removeEventListener('nav-badges-refresh', refresh); };
  }, [refresh]);

  const dateFr = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const alertes = (data?.alertes || []).filter(a => pageOfScreen(tree, a.screen));
  const actus = data?.actualites || [];
  const lues = useLues(user);
  const nonLues = actus.filter(a => !lues.includes(a.id)).length;
  const newsOk = pageOfScreen(tree, 'news');
  const total = alertes.reduce((acc, a) => acc + (a.n || 0), 0) + nonLues;
  let branchIdx = 0;

  return (
    <aside className={`opn-fx__timeline ${total > 0 ? 'has-alerts' : ''}`} aria-label="Fil du jour" data-testid="fx-fil-du-jour">
      <header className="opn-tl__head">
        <div>
          <span className="opn-tl__kicker"><Radio className="h-3 w-3" /> Fil du jour</span>
          <h2 className="opn-fx__date">{dateFr}</h2>
        </div>
        {data && <span className="opn-tl__total" data-testid="fx-total-alertes"><b>{total}</b><small>à traiter</small></span>}
      </header>
      <ol className="opn-tl">
        {!data && <li className="opn-tl__foot"><span className="opn-tl__node opn-tl__node--muted" /><span className="text-xs text-[var(--opn-muted)]">Chargement…</span></li>}
        {alertes.map((a, i) => {
          const tone = a.items?.length ? TONES[branchIdx++ % 2] : 'cyan';
          return <Event key={a.id} n={a.n} label={a.label} detail={a.detail} items={a.items} delay={i * 60} tone={tone}
            testId={`fx-alerte-${a.id}`} onClick={() => onGoScreen(a.screen, a.ecran)} onItem={setSelected} />;
        })}
        {data && newsOk && (
          <Event n={actus.length} alert={nonLues > 0} label="Actualités" icon={<Newspaper className="h-3.5 w-3.5" />} delay={alertes.length * 60} tone="cyan"
            detail={actus.length === 0 ? 'Aucune publication' : `${nonLues} non lue${nonLues > 1 ? 's' : ''}`}
            testId="fx-alerte-actualites" onClick={() => onGoScreen('news')} />
        )}
        <li className="opn-tl__foot"><span className="opn-tl__node opn-tl__node--muted" /><span className="opn-tl__foot-txt"><Clock className="h-3 w-3 opn-tl__spin" /> Mise à jour automatique{majA && <em> · {majA.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</em>}</span></li>
      </ol>
      <FilDuJourDetail item={selected} user={user} onClose={() => setSelected(null)} onGo={onGoScreen} onChanged={refresh} />
    </aside>
  );
};

export default FilDuJour;
